// One planned announcement in the list: the legacy DesktopListItem and
// MobileListItem.
//
// A row is 70px tall and opens on a click to show the kind of announcement,
// start → end, the stand-in, and a column with edit, listen and delete. A live
// one is painted in success.light (success.dark in the dark theme) with white
// text, which is how the old list said "this is on your mailbox now" without a
// word.

import { Fragment, useEffect, useState } from "react";
import {
  Box,
  CardActionArea,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Paper,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DeleteIcon from "@mui/icons-material/Delete";
import EastIcon from "@mui/icons-material/East";
import EditIcon from "@mui/icons-material/Edit";
import { darken } from "polished";
import { api, ApiError, type Card, type FormDescriptor, type PreviewResult } from "~/api";
import { useT } from "~/i18n";
import { DefaultPrimary } from "~/theme";
import { AnnouncementIcon, full, modeFor, timeInformation, titleKey, untilOrSince } from "~/legacy/helpers";
import PlayIcon from "~/legacy/icons/PlayIcon";

export type Sound = {
  url: string;
  message: string;
  startsAt: string;
  endsAt?: string;
  noEnd: boolean;
};

/** isActive is the legacy test: started and not yet over. */
export function isActive(card: Card): boolean {
  const now = Date.now();
  const start = new Date(card.startsAt).getTime();
  const end = card.endsAt ? new Date(card.endsAt).getTime() : null;
  return start <= now && (end === null || end > now);
}

type Props = {
  card: Card;
  descriptor: FormDescriptor | undefined;
  onEdit: (card: Card) => void;
  onDone: () => void;
  onError: (message: string) => void;
  onSound: (sound: Sound) => void;
};

/** useRow is what both rows share: the words, the colours and the three actions. */
function useRow({ card, descriptor, onDone, onError, onSound }: Props) {
  const t = useT();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const live = isActive(card);
  const mode = modeFor(descriptor, card.icon);
  const title = t(titleKey(mode));
  const ink = live ? "#fff" : theme.palette.text.primary;
  const soft = live
    ? darken(0.12, "#fff")
    : theme.palette.mode === "light" && theme.palette.primary.main === DefaultPrimary
      ? "#4F4F4F"
      : theme.palette.text.secondary;
  const bg = live ? (theme.palette.mode === "dark" ? "success.dark" : "success.light") : "background.paper";
  const noTitle = !card.displayName;

  const firstLine = card.displayName
    ? open
      ? card.displayName
      : `${card.displayName} ${untilOrSince(card.startsAt, live, t)}`
    : untilOrSince(card.startsAt, live, t);
  const secondLine =
    open && card.displayName
      ? untilOrSince(card.startsAt, live, t)
      : timeInformation(card.startsAt, card.endsAt, mode, t);

  const remove = async () => {
    try {
      await api(`/api/portal/announcements/${card.id}`, { method: "DELETE" });
      onDone();
    } catch (e) {
      onError(e instanceof ApiError ? e.detail : t("something-went-wrong"));
    }
  };

  const listen = async () => {
    setLoading(true);
    try {
      const p = await api<PreviewResult>("/api/portal/announcements/preview", {
        method: "POST",
        body: { companyMessageId: card.companyMessageId, endsAt: card.endsAt ?? null, proxyUserId: card.proxy?.id ?? 0 },
      });
      onSound({ url: p.url, message: title, startsAt: card.startsAt, endsAt: card.endsAt, noEnd: mode === "open" });
    } catch (e) {
      onError(e instanceof ApiError ? e.detail : t("something-went-wrong"));
    } finally {
      setLoading(false);
    }
  };

  const icon = (
    <AnnouncementIcon
      icon={card.icon}
      color={ink}
      bgColor={live ? (theme.palette.mode === "dark" ? theme.palette.success.dark : theme.palette.success.light) : theme.palette.background.paper}
    />
  );

  return { t, theme, open, setOpen, loading, live, title, ink, soft, bg, noTitle, firstLine, secondLine, remove, listen, icon };
}

export function DesktopListItem(props: Props & { paperWidth: number }) {
  const { card, onEdit, paperWidth } = props;
  const { t, open, setOpen, loading, live, title, ink, soft, bg, noTitle, firstLine, secondLine, remove, listen, icon } =
    useRow(props);

  return (
    <TableRow sx={{ "&:first-of-type": { pt: 1 }, "&:last-child td, &:last-child th": { border: 0 } }}>
      <TableCell sx={{ p: 0, border: 0 }}>
        <Paper elevation={1} sx={{ borderRadius: 3, mb: 1, bgcolor: bg, width: paperWidth * 0.5 - 20 }}>
          <Collapse in={open} collapsedSize={70}>
            <Box height={280}>
              <CardActionArea sx={{ borderRadius: 3 }}>
                <Grid
                  container
                  onClick={() => setOpen(!open)}
                  sx={{
                    color: live ? "#fff" : "text.primary",
                    py: 2,
                    borderBottom: open ? 1 : 0,
                    borderBottomColor: "divider",
                    cursor: "pointer",
                  }}
                >
                  <Grid item md={3} lg={2} xl={1.5} sx={{ pl: 2 }}>
                    {icon}
                  </Grid>
                  <Grid item sm={8} md={8} lg={8.5}>
                    <Grid container direction="column">
                      <Typography
                        noWrap
                        sx={{
                          pl: open && noTitle ? 5.5 : 1,
                          pt: open && noTitle ? 1 : 0,
                          width: "90%",
                          fontWeight: open ? 700 : 400,
                          fontSize: "16px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: "normal",
                          transform: open && noTitle ? "scale(1.4)" : "scale(1)",
                          transition: open && noTitle ? "transform 300ms ease" : "none",
                        }}
                      >
                        {firstLine}
                      </Typography>
                      <Typography
                        noWrap
                        sx={{
                          pl: 1,
                          width: "90%",
                          display: open && noTitle ? "none" : "inline",
                          color: soft,
                          fontSize: "16px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: "normal",
                        }}
                      >
                        {secondLine}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid item xs={1}>
                    <Box sx={{ pt: 1.5, display: "flex", justifyContent: "center" }}>
                      <ArrowBackIosNewIcon
                        sx={{
                          color: live ? "#fff" : "primary.main",
                          fontSize: 19,
                          transform: open ? "rotate(90deg)" : "rotate(180deg)",
                          transition: "transform 200ms ease",
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardActionArea>
              <Grid container>
                <Box width="80%" sx={{ pl: 2 }}>
                  <Box sx={{ my: 2 }}>
                    <Typography noWrap sx={{ width: "95%", color: ink }}>
                      {title}
                    </Typography>
                  </Box>
                  <Grid container sx={{ py: 3, borderBottom: 1, borderTop: 1, borderColor: "divider" }}>
                    <Grid item xs={4} lg={4} sx={{ textAlign: "center" }}>
                      <Typography sx={{ color: ink, fontSize: 15 }}>{full(card.startsAt)}</Typography>
                    </Grid>
                    <Grid item xs={4} lg={4} sx={{ color: ink, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <EastIcon />
                    </Grid>
                    <Grid item xs={4} lg={4} sx={{ textAlign: "center", pr: card.endsAt ? 0 : 1, overflow: "hidden" }}>
                      <Typography sx={{ color: ink, fontSize: 15, pr: 1 }}>
                        {card.endsAt ? full(card.endsAt) : t("no-enddate")}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Representation card={card} live={live} desktop />
                </Box>
                <Box
                  width="20%"
                  height={180}
                  visibility={open ? "visible" : "hidden"}
                  sx={{
                    color: ink,
                    borderLeft: 1,
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    pr: 2,
                  }}
                >
                  <Grid container direction="column" alignContent="center">
                    <Grid item>
                      <Tooltip title={t("edit")} placement="right">
                        <IconButton onClick={() => onEdit(card)} sx={{ mt: 0.5 }}>
                          <EditIcon sx={{ color: ink, fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      {!loading ? (
                        <Tooltip title={t("test-listen")} placement="right">
                          <Box sx={{ my: 2.25, display: "flex", justifyContent: "center" }}>
                            <IconButton onClick={listen} sx={{ p: 0.75, border: 1.5, borderColor: ink }}>
                              <PlayIcon color={ink} fontSize={16} />
                            </IconButton>
                          </Box>
                        </Tooltip>
                      ) : (
                        <CircularProgress color="inherit" size={24} sx={{ my: 2.3, ml: 0.5 }} />
                      )}
                    </Grid>
                    <Grid item>
                      <Tooltip title={t("delete")} placement="right">
                        <IconButton onClick={remove} sx={{ color: ink, "&:hover": { color: "error.main" } }}>
                          <DeleteIcon sx={{ color: "inherit", fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </TableCell>
    </TableRow>
  );
}

export function MobileListItem(props: Props) {
  const { card, onEdit } = props;
  // The legacy row measured the window, not its container.
  const [width, setWidth] = useState(window.innerWidth * 0.85);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth * 0.85);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const { t, theme, open, setOpen, loading, live, title, ink, soft, bg, noTitle, firstLine, secondLine, remove, listen, icon } =
    useRow(props);

  return (
    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
      <TableCell sx={{ p: 0, border: 0 }}>
        <Paper
          elevation={theme.palette.mode === "dark" ? 1 : 0}
          sx={{
            borderRadius: 3,
            mb: 1,
            border: theme.palette.mode === "dark" ? 0 : 1.5,
            borderColor: "divider",
            bgcolor: bg,
          }}
        >
          <Collapse in={open} collapsedSize={70}>
            <Box height={260}>
              <CardActionArea sx={{ borderRadius: 3 }}>
                <Grid
                  container
                  onClick={() => setOpen(!open)}
                  sx={{
                    width,
                    color: live ? "#fff" : "text.primary",
                    p: 2,
                    pl: 0,
                    borderBottom: open ? 1 : 0,
                    borderBottomColor: "divider",
                    cursor: "pointer",
                    ml: 1,
                  }}
                >
                  <Grid item xs={2} sx={{ pt: open ? 0.8 : 0.5 }}>
                    {icon}
                  </Grid>
                  <Grid item xs={9.5}>
                    <Grid container direction="column">
                      <Typography
                        noWrap
                        sx={{
                          pl: open && noTitle ? 4.5 : 1,
                          pt: open && noTitle ? 1.75 : 0.5,
                          width: "90%",
                          fontWeight: open ? 700 : 400,
                          fontSize: open && noTitle ? "16px" : "14px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: "normal",
                          transform: open && noTitle ? "scale(1.4)" : "scale(1)",
                          transition: open && noTitle ? "transform 300ms ease" : "none",
                        }}
                      >
                        {firstLine}
                      </Typography>
                      <Typography
                        noWrap
                        sx={{
                          pl: 1,
                          width: "90%",
                          display: open && noTitle ? "none" : "inline",
                          color: soft,
                          fontSize: "14px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: "normal",
                        }}
                      >
                        {secondLine}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid item xs={0.5}>
                    <Box sx={{ pt: 1.5, pl: 1 }}>
                      <ArrowBackIosNewIcon
                        sx={{
                          color: live ? "#fff" : "primary.main",
                          fontSize: 19,
                          transform: open ? "rotate(90deg)" : "rotate(180deg)",
                          transition: "transform 200ms ease",
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardActionArea>
              <Grid container>
                <Box width="80%" sx={{ pl: 2 }}>
                  <Box sx={{ borderBottom: 1, borderColor: "divider", pt: 1.5, pb: 1 }}>
                    <Typography noWrap sx={{ width: "95%", color: ink }}>
                      {title}
                    </Typography>
                  </Box>
                  <Grid container sx={{ py: 2, borderBottom: 1, borderColor: "divider" }}>
                    <Grid item xs={4} sx={{ textAlign: "center" }}>
                      <Typography sx={{ color: ink, fontSize: 15 }}>{full(card.startsAt)}</Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ color: ink, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <EastIcon />
                    </Grid>
                    <Grid item xs={4} sx={{ textAlign: "center", pr: card.endsAt ? 0 : 1 }}>
                      <Typography sx={{ color: ink, fontSize: 15 }}>
                        {card.endsAt ? full(card.endsAt) : t("no-enddate")}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Representation card={card} live={live} />
                </Box>
                <Box
                  width="20%"
                  height={165}
                  visibility={open ? "visible" : "hidden"}
                  sx={{
                    color: ink,
                    borderLeft: 1,
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Grid container direction="column">
                    <Tooltip title={t("edit")} placement="right">
                      <IconButton onClick={() => onEdit(card)} sx={{ mx: 2 }}>
                        <EditIcon sx={{ color: ink, fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    {!loading ? (
                      <Tooltip title={t("test-listen")} placement="right">
                        <Box sx={{ height: 32, my: 3, display: "flex", justifyContent: "center" }}>
                          <IconButton onClick={listen} sx={{ p: 1, border: 1.5, borderColor: ink }}>
                            <PlayIcon color={ink} fontSize={14} />
                          </IconButton>
                        </Box>
                      </Tooltip>
                    ) : (
                      <CircularProgress color="inherit" size={26} sx={{ my: 3.33, ml: 3 }} />
                    )}
                    <Tooltip title={t("delete")} placement="right">
                      <IconButton onClick={remove} sx={{ color: ink, "&:hover": { color: "error.main" }, mx: 2 }}>
                        <DeleteIcon sx={{ color: "inherit", fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Box>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </TableCell>
    </TableRow>
  );
}

function Representation({ card, live, desktop = false }: { card: Card; live: boolean; desktop?: boolean }) {
  const t = useT();
  const name = card.proxy?.name;
  if (!name) {
    return (
      <Typography noWrap={desktop} sx={{ color: live ? "#fff" : "text.secondary", fontSize: 12, mt: 1 }}>
        {t("no-representation")}
      </Typography>
    );
  }
  return (
    <Fragment>
      <Typography noWrap={desktop} sx={{ color: live ? "#fff" : "text.secondary", fontSize: 12, mt: 1 }}>
        {t("representation")}
      </Typography>
      <Typography noWrap={desktop} sx={{ color: live ? "#fff" : "text.primary", fontSize: 14 }}>
        {name.length > 28 ? `${name.substring(0, 28)} ...` : name}
      </Typography>
    </Fragment>
  );
}
